import { useState } from "react";
import type { CardPerformanceDetail } from "../../../generated/graphql";

interface CardPerformanceTableProps {
  cards: CardPerformanceDetail[];
  onSort?: (field: string, order: string) => void;
}

export function CardPerformanceTable({ cards, onSort }: CardPerformanceTableProps) {
  const [sortField, setSortField] = useState("difficulty");
  const [sortOrder, setSortOrder] = useState("desc");

  const handleSort = (field: string) => {
    const newOrder = sortField === field && sortOrder === "desc" ? "asc" : "desc";
    setSortField(field);
    setSortOrder(newOrder);
    onSort?.(field, newOrder);
  };

  return (
    <div className="data-table-container">
      <div className="data-table-header">
        <h4 className="h6">Card Performance ({cards.length} cards)</h4>
      </div>
      <div className="overflow-x-auto gradient-scrollbar">
        <table className="data-table">
          <thead>
            <tr>
              <th className="sortable" onClick={() => handleSort("word")}>
                Word {sortField === "word" && (sortOrder === "asc" ? "↑" : "↓")}
              </th>
              <th>State</th>
              <th className="sortable" onClick={() => handleSort("difficulty")}>
                Difficulty {sortField === "difficulty" && (sortOrder === "asc" ? "↑" : "↓")}
              </th>
              <th className="sortable" onClick={() => handleSort("retention")}>
                Retention {sortField === "retention" && (sortOrder === "asc" ? "↑" : "↓")}
              </th>
              <th>Reviews</th>
              <th>Lapses</th>
              <th>Next Review</th>
            </tr>
          </thead>
          <tbody>
            {cards.map((card) => (
              <tr key={card.cardId}>
                <td>
                  <div className="data-table-cell-name">{card.word}</div>
                </td>
                <td>
                  <span
                    className={`px-2 py-1 text-xs font-semibold rounded ${
                      card.state === "NEW"
                        ? "badge-secondary"
                        : card.state === "LEARNING"
                          ? "badge-accent"
                          : card.state === "REVIEW"
                            ? "badge-primary"
                            : "badge-danger"
                    }`}
                  >
                    {card.state}
                  </span>
                </td>
                <td>
                  <span
                    className={`px-2 py-1 text-xs rounded ${
                      card.difficulty >= 7
                        ? "badge-danger"
                        : card.difficulty >= 4
                          ? "badge-accent"
                          : "badge-primary"
                    }`}
                  >
                    {card.difficulty.toFixed(1)}
                  </span>
                </td>
                <td>
                  <div className="flex items-center">
                    <div className="flex-1 h-2 bg-gray-100 dark:bg-gray-800 rounded-full mr-2 max-w-[100px]">
                      <div
                        className={`h-2 rounded-full ${
                          card.retention >= 0.8
                            ? "bg-success-500"
                            : card.retention >= 0.6
                              ? "bg-warning-500"
                              : "bg-error-500"
                        }`}
                        style={{ width: `${card.retention * 100}%` }}
                      />
                    </div>
                    <span className="data-table-cell-caption">
                      {(card.retention * 100).toFixed(0)}%
                    </span>
                  </div>
                </td>
                <td>
                  <span className="data-table-cell-caption">{card.reviewCount}</span>
                </td>
                <td>
                  <span
                    className={`data-table-cell-caption ${card.lapseCount > 0 ? "text-error-500 font-medium" : ""}`}
                  >
                    {card.lapseCount}
                  </span>
                </td>
                <td>
                  <span className="data-table-cell-caption">
                    {card.nextReview ? new Date(card.nextReview).toLocaleDateString() : "—"}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
